// Подстановка `:param` в путь ручки и сериализация остатка `input` в query-строку.

import { isPlainObject } from '~/shared/utils'

export type BuildUrlResult = {
  /** Путь с подставленными :param, например `/users/42` */
  pathname: string
  /** Query-строка вместе с `?`, либо '' */
  search: string
  /** pathname + search */
  url: string
}

const PATH_PARAM_RE = /:([a-zA-Z0-9_]+)/g

/** Заменяет `:param` в пути на значения из `input`. Бросает, если значения не хватает */
export function substitutePathParams(
  path: string,
  input: unknown,
): { pathname: string; usedKeys: Set<string> } {
  const usedKeys = new Set<string>()
  const record = isPlainObject(input) ? input : {}

  const pathname = path.replace(PATH_PARAM_RE, (match, key: string) => {
    const value = record[key]

    if (value === undefined || value === null) {
      throw new Error(`buildUrl: отсутствует значение параметра "${match}" для пути "${path}"`)
    }

    usedKeys.add(key)
    return encodeURIComponent(String(value))
  })

  return { pathname, usedKeys }
}

function appendSearchParam(search: URLSearchParams, key: string, value: unknown): void {
  if (value === undefined || value === null) return

  if (Array.isArray(value)) {
    value.forEach((item) => appendSearchParam(search, key, item))
    return
  }

  if (value instanceof Date) {
    search.append(key, value.toISOString())
    return
  }

  if (typeof value === 'object') {
    search.append(key, JSON.stringify(value))
    return
  }

  search.append(key, String(value))
}

/** Сериализует объект в query-строку (с `?`), отбрасывая `undefined`/`null` */
export function buildSearchString(params: Record<string, unknown> | undefined): string {
  if (!params) return ''

  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    appendSearchParam(search, key, value)
  }

  const serialized = search.toString()
  return serialized ? `?${serialized}` : ''
}

export type BuildUrlOptions = {
  isGet: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getSearchParams?: (input: any) => Record<string, unknown>
}

/**
 * Строит итоговый путь ручки: подставляет `:param`, для GET — добавляет query
 * (по умолчанию — весь `input` за вычетом path-параметров).
 */
export function buildUrl(path: string, input: unknown, options: BuildUrlOptions): BuildUrlResult {
  const { pathname, usedKeys } = substitutePathParams(path, input)

  if (!options.isGet) {
    return { pathname, search: '', url: pathname }
  }

  const record = isPlainObject(input) ? input : {}

  const searchInput = options.getSearchParams
    ? options.getSearchParams(input)
    : Object.fromEntries(Object.entries(record).filter(([key]) => !usedKeys.has(key)))

  const search = buildSearchString(searchInput)

  return { pathname, search, url: pathname + search }
}
